/**
 * Unified Raid Route
 *
 * Manages all in-raid phases with a persistent PixiJS application.
 * The canvas is always visible in background, with phase-specific UI overlays.
 *
 * Phases handled:
 * - starting/loading: Loading screen with tips
 * - room_selection: Room voting UI overlay
 * - draft: Pokemon selection UI overlay
 * - battle_starting/in_battle: Battle scene with HUD
 * - battle_result: Battle outcome display
 * - finished: Rewards screen
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { flushSync } from "react-dom";
import {
  Application,
  Assets,
  Cache,
  Container,
  Graphics,
  Sprite,
} from "pixi.js";
import { CompleteBattleLog } from "types/battle-engine";
import {
  gridMarginX,
  gridMarginY,
  gridTileSize,
  GRID_WIDTH,
  GRID_HEIGHT,
} from "types/battle-engine/constants";
import { Battle_Action } from "types/battle-engine/battle";
import {
  buildAssetManifest,
  preloadBattleAssets,
} from "@/lib/battle/asset-loader";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { BattleReplay } from "@/lib/battle/BattleReplay";
import { BattleStatsHUD } from "@/components/battle/BattleStatsHUD";
import {
  useRaid,
  CoopRaidState,
  RoomSelectionState,
  RaidLobbyPlayer,
} from "@/providers/raid-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { RouteNames } from "@/types/route-names";
import { StrokedText } from "@/components/ui/text/stroked-text";
import classNames from "classnames";
import { S3_BASE_URL } from "@/utils/s3";
import { PokeNames } from "types/pokemon/poke-names";
import { PokeType } from "types/pokemon/poke-type";
import { RoomDifficulty, BossTier, RaidRoomOption } from "types/raids/rooms";
import { PlayerRole, RaidPokemon } from "types/raid-dynamodb";

import { RaidIntroView } from "@/views/raid/raid-intro-view";

// Phase-specific overlay components (imported from existing views)
import { RaidRoomSelectionOverlayV2 } from "@/views/raid/raid-room-selection-overlay-v2";
import { RaidTeamBuildPixiOverlay } from "@/views/raid/raid-team-build-pixi-overlay";
import { RaidLoadingOverlay } from "@/views/raid/raid-loading-view";
import { RaidRewardsOverlay } from "@/views/raid/raid-rewards-view";
import { RaidLobbyView } from "@/views/raid/raid-lobby-view";
import { RoomSelectionScene } from "@/lib/raid/room-selection-scene";
import { RoomSelectionSceneV2 } from "@/lib/raid/room-selection-scene-v2";
import { isXsScreen } from "@/utils/screen-size-check";
import { RaidRoomClearTransition } from "@/components/raid/raid-room-clear-transition";
import { PokemonBattleHUD } from "@/components/battle/PokemonBattleHUD";

// ============================================================================
// Types
// ============================================================================

type BattlePhase = "idle" | "loading" | "ready" | "playing" | "completed";

interface PreloadState {
  isPreloading: boolean;
  progress: number;
  preloadedPokemon: Set<string>;
}

// ============================================================================
// Main Component
// ============================================================================

const Raid: React.FC = () => {
  const { setCurrentRoute } = useSPAContext();
  const { raidState, acknowledgeBattleEnd } = useRaid();

  // PixiJS persistent state
  const [app, setApp] = useState<Application | null>(null);
  const [world, setWorld] = useState<Container | null>(null);
  const [overlayWorld, setOverlayWorld] = useState<Container | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Battle-specific state
  const [pokemons, setPokemons] = useState<Record<string, ClientPokemon>>({});
  const [battleLog, setBattleLog] = useState<CompleteBattleLog | null>(null);
  const [battlePhase, setBattlePhase] = useState<BattlePhase>("idle");
  const [currentTick, setCurrentTick] = useState(0);
  const [currentBattleUrl, setCurrentBattleUrl] = useState<string | null>(null);
  const hasStartedBattle = useRef(false);

  const [selectedPokemonId, setSelectedPokemonId] = useState<string | null>(
    null,
  );

  const pokemonIds = useMemo(() => Object.keys(pokemons), [pokemons]);

  // Initial selection
  useEffect(() => {
    if (!selectedPokemonId && pokemonIds.length > 0) {
      setSelectedPokemonId(pokemonIds[0] ?? null);
    }
  }, [pokemonIds, selectedPokemonId]);

  const handleNext = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[0] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const nextIndex = (currentIndex + 1) % pokemonIds.length;
      return pokemonIds[nextIndex] ?? null;
    });
  }, [pokemonIds]);

  const handlePrevious = useCallback(() => {
    setSelectedPokemonId((prev) => {
      if (!prev) return pokemonIds[pokemonIds.length - 1] ?? null;
      const currentIndex = pokemonIds.indexOf(prev);
      const prevIndex =
        (currentIndex - 1 + pokemonIds.length) % pokemonIds.length;
      return pokemonIds[prevIndex] ?? null;
    });
  }, [pokemonIds]);

  // Preloading state
  const [preloadState, setPreloadState] = useState<PreloadState>({
    isPreloading: false,
    progress: 0,
    preloadedPokemon: new Set(),
  });

  // Room Selection Scene (PIXI-based) - Old version for draft expansion
  const roomSelectionSceneRef = useRef<RoomSelectionScene | null>(null);
  // Room Selection Scene V2 (immersive crossroads view)
  const roomSelectionSceneV2Ref = useRef<RoomSelectionSceneV2 | null>(null);
  const [isRoomSelectionSceneReady, setIsRoomSelectionSceneReady] =
    useState(false);
  const [showRoomSelectionOverlay, setShowRoomSelectionOverlay] =
    useState(true);
  // Track if we've passed through the intro phase for this room_selection status
  const hasCompletedIntroRef = useRef(false);
  // Track if room expansion has been triggered (to prevent duplicate calls)
  const hasTriggeredExpansionRef = useRef(false);

  // Extract raid state values
  const {
    status,
    current_battle,
    current_round,
    is_boss_round,
    draft,
    result,
  } = raidState;

  // Visual State
  const [visualPhase, setVisualPhase] = useState<
    "idle" | "intro" | "room_reveal"
  >("idle");
  const [cachedRoomSelection, setCachedRoomSelection] =
    useState<RoomSelectionState | null>(null);
  const prevStatusRef = useRef(status);

  // Cache room selection for the reveal animation (since it clears in draft)
  useEffect(() => {
    if (raidState.room_selection) {
      setCachedRoomSelection(raidState.room_selection);
    }
  }, [raidState.room_selection]);

  // Visual State Machine
  useEffect(() => {
    if (prevStatusRef.current !== status) {
      const prev = prevStatusRef.current;

      // 1. Intro (Starting -> Room Selection) - Only on first cycle
      if (status === "room_selection") {
        // Reset expansion flag for new room selection cycle
        hasTriggeredExpansionRef.current = false;

        if (prev === "starting" || prev === "lobby" || prev === "idle") {
          // First cycle - show intro
          hasCompletedIntroRef.current = false;
          setVisualPhase("intro");
        } else if (prev === "battle_result") {
          // Subsequent cycles - skip intro, go directly to room selection
          hasCompletedIntroRef.current = true;
          setVisualPhase("idle");
        }
      }

      // 2. Room Reveal (Room Selection -> Draft)
      if (status === "draft" && prev === "room_selection") {
        setVisualPhase("room_reveal");
        // Auto-clear reveal after animation
        setTimeout(() => {
          setVisualPhase((curr) => (curr === "room_reveal" ? "idle" : curr));
        }, 3000);
      }

      // Reset visual phase on other transitions just in case
      if (status === "lobby") setVisualPhase("idle");

      prevStatusRef.current = status;
    }
  }, [status]);

  // Derived data for visuals
  const selectedRoomIndex = useMemo(() => {
    if (status !== "draft" || !draft?.selected_room || !cachedRoomSelection)
      return null;
    return cachedRoomSelection.room_options.findIndex(
      (r) => r.id === draft.selected_room.id,
    );
  }, [status, draft, cachedRoomSelection]);

  // ============================================================================
  // PixiJS Initialization (persistent across all phases)
  // ============================================================================

  useEffect(() => {
    let localApp: Application | null = null;
    let isCancelled = false;

    const initApp = async () => {
      const gameContainer = containerRef.current;
      if (!gameContainer || isCancelled) return;

      // Check for existing canvas
      const existingCanvas = gameContainer.querySelector("canvas");
      if (existingCanvas) return;

      try {
        localApp = new Application();

        // Load font
        try {
          await document.fonts?.load?.('16px "vck"');
        } catch {
          // Font loading failed, continue anyway
        }

        if (isCancelled) return;

        await localApp.init({
          background: "#161e29",
          resizeTo: gameContainer,
          antialias: false,
        });

        if (isCancelled) {
          localApp.destroy(true);
          return;
        }

        gameContainer.appendChild(localApp.canvas);
        localApp.stage.sortableChildren = true;

        // Create world container
        const worldContainer = new Container();
        worldContainer.sortableChildren = true;
        localApp.stage.addChild(worldContainer);

        // Create overlay container (static, on top of world)
        const overlayContainer = new Container();
        overlayContainer.sortableChildren = true;
        localApp.stage.addChild(overlayContainer);

        // Setup camera/scaling - fit map (480x960) to canvas, max scale 1
        const mapWidth = 480;
        const mapHeight = 960;
        const scaleX = gameContainer.clientWidth / mapWidth;
        const scaleY = gameContainer.clientHeight / mapHeight;
        const scale = Math.min(scaleX, scaleY, 1);

        worldContainer.scale.set(scale);
        worldContainer.position.set(
          (gameContainer.clientWidth - mapWidth * scale) / 2,
          (gameContainer.clientHeight - mapHeight * scale) / 2,
        );

        overlayContainer.scale.set(scale);
        overlayContainer.position.set(
          (gameContainer.clientWidth - mapWidth * scale) / 2,
          (gameContainer.clientHeight - mapHeight * scale) / 2,
        );

        setApp(localApp);
        setWorld(worldContainer);
        setOverlayWorld(overlayContainer);
        console.log("[Raid] PixiJS app initialized");
      } catch (error) {
        console.error("[Raid] PixiJS init failed:", error);
      }
    };

    initApp();

    return () => {
      isCancelled = true;
      if (localApp) {
        try {
          localApp.ticker?.stop();
          localApp.canvas?.remove();
          localApp.stage?.destroy({ children: true, texture: true });
          localApp.destroy(true, { children: true, texture: true });
        } catch (e) {
          console.warn("[Raid] Cleanup error:", e);
        }
      }
      setApp(null);
      setWorld(null);
      setOverlayWorld(null);
      setPokemons({});
      console.log("[Raid] PixiJS app destroyed");
    };
  }, []);

  // ============================================================================
  // Room Selection Scene V2 Management (Immersive Crossroads View)
  // ============================================================================

  useEffect(() => {
    // Initialize room selection scene V2 when entering room_selection status
    // Wait for intro to complete before initializing
    if (
      status === "room_selection" &&
      visualPhase === "idle" &&
      hasCompletedIntroRef.current && // Must have completed intro
      app &&
      world &&
      raidState.room_selection?.room_options &&
      !roomSelectionSceneV2Ref.current
    ) {
      const initScene = async () => {
        // Clear any existing children from world container
        console.log(
          "[Raid] World children before clear:",
          world.children.length,
        );
        world.removeChildren();
        console.log(
          "[Raid] World children after clear:",
          world.children.length,
        );

        // Reset world transform for room selection (full screen, no battle grid offset)
        world.scale.set(1);
        world.position.set(0, 0);

        console.log(
          "[Raid] Initializing room selection scene V2, screen:",
          app.screen.width,
          "x",
          app.screen.height,
        );

        // Use V2 immersive crossroads scene
        const sceneV2 = new RoomSelectionSceneV2(app, world);
        roomSelectionSceneV2Ref.current = sceneV2;

        await sceneV2.initialize(
          raidState.room_selection!.room_options,
          raidState.players,
        );
        setIsRoomSelectionSceneReady(true);
        setShowRoomSelectionOverlay(true);
      };

      initScene();
    }

    // Update votes in V2 scene when votes change
    if (
      status === "room_selection" &&
      roomSelectionSceneV2Ref.current &&
      raidState.room_selection?.votes
    ) {
      roomSelectionSceneV2Ref.current.updateVotes(
        raidState.room_selection.votes,
      );
    }

    // Handle transition from room_selection to draft
    // Destroy V2 scene and initialize V1 scene for draft phase expansion
    if (
      status === "draft" &&
      selectedRoomIndex !== null &&
      !hasTriggeredExpansionRef.current
    ) {
      hasTriggeredExpansionRef.current = true;

      const transitionToDraft = async () => {
        // Fade out overlay first
        setShowRoomSelectionOverlay(false);

        // Wait for overlay fade
        await new Promise((r) => setTimeout(r, 300));

        // Destroy V2 scene
        if (roomSelectionSceneV2Ref.current) {
          roomSelectionSceneV2Ref.current.destroy();
          roomSelectionSceneV2Ref.current = null;
        }

        // Clear world for V1 scene
        world?.removeChildren();

        // Initialize V1 scene for draft phase (with expandable rooms)
        if (app && world && cachedRoomSelection?.room_options) {
          const scene = new RoomSelectionScene(app, world);
          roomSelectionSceneRef.current = scene;

          await scene.initialize(cachedRoomSelection.room_options);

          // Immediately expand to the selected room
          await scene.expandRoom(selectedRoomIndex);
        }
      };

      transitionToDraft();
    }

    // Cleanup scenes when leaving raid phases entirely
    if (
      status !== "room_selection" &&
      status !== "draft" &&
      status !== "battle_starting" &&
      status !== "in_battle"
    ) {
      if (roomSelectionSceneV2Ref.current) {
        roomSelectionSceneV2Ref.current.destroy();
        roomSelectionSceneV2Ref.current = null;
      }
      if (roomSelectionSceneRef.current) {
        roomSelectionSceneRef.current.destroy();
        roomSelectionSceneRef.current = null;
      }
      setIsRoomSelectionSceneReady(false);
      setShowRoomSelectionOverlay(true);
    }
  }, [
    status,
    visualPhase,
    app,
    world,
    raidState.room_selection,
    raidState.players,
    cachedRoomSelection,
    selectedRoomIndex,
  ]);

  // ============================================================================
  // Asset Preloading (during room_selection/draft phases)
  // ============================================================================

  useEffect(() => {
    if (!app || !draft?.available_pokemon) return;

    // Extract Pokemon names that haven't been preloaded yet
    const pokemonNames = draft.available_pokemon
      .map((p) => p.base_pokemon_name)
      .filter((name) => !preloadState.preloadedPokemon.has(name));

    if (pokemonNames.length === 0) return;

    const preloadPokemonAssets = async () => {
      setPreloadState((prev) => ({ ...prev, isPreloading: true, progress: 0 }));

      const total = pokemonNames.length;
      let loaded = 0;

      for (const pokemonName of pokemonNames) {
        try {
          // Load AnimData.xml
          const xmlPath = `${S3_BASE_URL}/pokemons/${pokemonName}/AnimData.xml`;
          const xmlText = await fetch(xmlPath, { mode: "cors" })
            .then((res) => res.text())
            .catch(() => "");

          if (xmlText) {
            // Parse XML to get animation names
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlText, "text/xml");
            const anims = xmlDoc.querySelectorAll("Anims > Anim");
            const basePath = xmlPath.replace("/AnimData.xml", "");

            // Load animation PNGs
            for (const anim of Array.from(anims)) {
              const animName = anim.getAttribute("Name");
              if (!animName) continue;

              const pngPath = `${basePath}/${animName}-Anim.png`;
              if (!Cache.has(pngPath)) {
                try {
                  await Assets.load({
                    src: pngPath,
                    data: {
                      resourceOptions: { crossorigin: "anonymous" },
                      loadOptions: {
                        fetch: { mode: "cors", credentials: "omit" },
                      },
                    },
                  });
                } catch {
                  // Silently ignore failed animation loads
                }
              }
            }
          }

          loaded++;
          setPreloadState((prev) => ({
            ...prev,
            progress: (loaded / total) * 100,
            preloadedPokemon: new Set([...prev.preloadedPokemon, pokemonName]),
          }));
        } catch (error) {
          console.warn(`[Raid] Failed to preload ${pokemonName}:`, error);
          loaded++;
        }
      }

      setPreloadState((prev) => ({ ...prev, isPreloading: false }));
      console.log(`[Raid] Preloaded ${pokemonNames.length} Pokemon assets`);
    };

    preloadPokemonAssets();
  }, [app, draft?.available_pokemon]);

  // ============================================================================
  // Battle Log Fetching
  // ============================================================================

  useEffect(() => {
    if (
      status !== "in_battle" ||
      !current_battle?.battle_log_url ||
      current_battle.battle_log_url === currentBattleUrl
    ) {
      return;
    }

    const fetchBattleLog = async () => {
      try {
        setCurrentBattleUrl(current_battle.battle_log_url);
        setBattlePhase("loading");
        hasStartedBattle.current = false;

        // Check if we have an existing scene from room selection/draft
        const hasExistingScene =
          !!roomSelectionSceneRef.current?.getExpandedSection();

        // Clean up previous battle state (only if no existing scene to preserve)
        if (!hasExistingScene) {
          if (world) {
            while (world.children.length > 0) {
              const child = world.children[0];
              if (child) {
                world.removeChild(child);
                child.destroy({ children: true });
              }
            }
          }
          if (overlayWorld) {
            while (overlayWorld.children.length > 0) {
              const child = overlayWorld.children[0];
              if (child) {
                overlayWorld.removeChild(child);
                child.destroy({ children: true });
              }
            }
          }
        } else {
          // Only clean up overlay world (we'll add overlay sprite fresh)
          if (overlayWorld) {
            while (overlayWorld.children.length > 0) {
              const child = overlayWorld.children[0];
              if (child) {
                overlayWorld.removeChild(child);
                child.destroy({ children: true });
              }
            }
          }
        }

        setPokemons({});
        setBattleLog(null);

        const response = await fetch(current_battle.battle_log_url);
        const data = (await response.json()) as CompleteBattleLog;
        setBattleLog(data);
      } catch (error) {
        console.error("[Raid] Failed to fetch battle log:", error);
      }
    };

    fetchBattleLog();
  }, [
    status,
    current_battle?.battle_log_url,
    currentBattleUrl,
    world,
    overlayWorld,
  ]);

  // ============================================================================
  // Battle Setup
  // ============================================================================

  const setupBattle = useCallback(
    async (log: CompleteBattleLog) => {
      if (!app || !world) return;

      try {
        const gameContainer = containerRef.current;
        if (!gameContainer) throw new Error("Game container not found");

        // =====================================================================
        // Scene Continuity: Try to use existing RoomSection scene
        // =====================================================================
        const expandedSection =
          roomSelectionSceneRef.current?.getExpandedSection();

        if (expandedSection) {
          console.log("[Raid] Using scene continuity - reusing draft scene");

          // Prepare the section for battle (updates pokemon data, reparents to world)
          const { pokemons: existingPokemons, mapSprite } =
            expandedSection.prepareForBattle(log, world);

          // Load any additional assets that might be needed
          const manifest = buildAssetManifest(
            log.teamA.members.map((m) => m.pokemon),
            log.teamB.members.map((m) => m.pokemon),
          );
          await preloadBattleAssets(manifest, (progress) => {
            console.log(
              `[Raid] Battle assets: ${progress.percentage.toFixed(1)}%`,
            );
          });

          // Set up world transform for battle
          const mapWidth = 480;
          const mapHeight = 960;
          const scaleX = gameContainer.clientWidth / mapWidth;
          const scaleY = gameContainer.clientHeight / mapHeight;
          let scale = Math.min(scaleX, scaleY, 1);
          if (isXsScreen()) {
            scale = scale * 0.75;
          }

          world.scale.set(scale);
          world.position.set(
            (gameContainer.clientWidth - mapWidth * scale) / 2,
            (gameContainer.clientHeight - mapHeight * scale) / 2,
          );

          if (overlayWorld) {
            overlayWorld.scale.set(scale);
            overlayWorld.position.set(
              (gameContainer.clientWidth - mapWidth * scale) / 2,
              (gameContainer.clientHeight - mapHeight * scale) / 2,
            );
          }

          // Add map overlay
          const loadAssetIfNeeded = async (path: string) => {
            if (Cache.has(path)) return Cache.get(path);
            return Assets.load(path);
          };

          const mapGridCenterX = 480 / 2;
          const mapGridCenterY = 960 / 2;

          const overlayPath = `/assets/maps/overlay/default.webp`;
          const mapOverlay = await loadAssetIfNeeded(overlayPath);
          const mapOverlaySprite = new Sprite(mapOverlay);
          mapOverlaySprite.x = mapGridCenterX;
          mapOverlaySprite.y = mapGridCenterY;
          mapOverlaySprite.scale.set(0.65);
          mapOverlaySprite.anchor.set(0.5, 0.5);
          mapOverlaySprite.zIndex = 9999;

          if (overlayWorld) {
            overlayWorld.addChild(mapOverlaySprite);
          } else {
            world.addChild(mapOverlaySprite);
          }

          // Draw grid (optional debug)
          const grid = new Graphics();
          grid.alpha = 0.005;
          for (let y = 0; y < GRID_HEIGHT; y++) {
            for (let x = 0; x < GRID_WIDTH; x++) {
              const isEven = (x + y) % 2 === 0;
              const color = isEven ? 0x444444 : 0x666666;
              grid.rect(
                gridMarginX + x * gridTileSize,
                gridMarginY + y * gridTileSize,
                gridTileSize,
                gridTileSize,
              );
              grid.fill(color);
            }
          }
          grid.zIndex = -500;
          world.addChild(grid);

          // Setup interaction for existing pokemons
          Object.values(existingPokemons).forEach((p) => {
            p.setOnClick((clickedPokemon) => {
              console.log("Clicked pokemon:", clickedPokemon.id);
              setSelectedPokemonId(clickedPokemon.id.toString());
            });
          });

          // Clean up the RoomSelectionScene reference (section is now part of world)
          roomSelectionSceneRef.current = null;
          setIsRoomSelectionSceneReady(false);

          setPokemons(existingPokemons);
          flushSync(() => {
            setBattlePhase("ready");
          });

          console.log(
            `[Raid] Scene continuity complete - ${
              Object.keys(existingPokemons).length
            } pokemon ready`,
          );
          return;
        }

        // =====================================================================
        // Fallback: Full setup (no existing scene, e.g., reconnecting mid-battle)
        // =====================================================================
        console.log("[Raid] No existing scene - using full battle setup");

        // Build and load assets (many may already be preloaded)
        const manifest = buildAssetManifest(
          log.teamA.members.map((m) => m.pokemon),
          log.teamB.members.map((m) => m.pokemon),
        );

        await preloadBattleAssets(manifest, (progress) => {
          console.log(
            `[Raid] Battle assets: ${progress.percentage.toFixed(1)}%`,
          );
        });

        // Load map
        const mapName =
          log.mapId ?? `map (${1 + Math.floor(Math.random() * 65)})`;

        const loadAssetIfNeeded = async (path: string) => {
          if (Cache.has(path)) return Cache.get(path);
          return Assets.load(path);
        };

        // Map sprite
        const mapPath = `/assets/maps/${mapName}.webp`;
        const mapImage = await loadAssetIfNeeded(mapPath);
        const mapSprite = new Sprite(mapImage);

        const mapGridWidth = GRID_WIDTH * gridTileSize;
        const mapGridHeight = GRID_HEIGHT * gridTileSize;
        const mapGridCenterX = gridMarginX + mapGridWidth / 2;
        const mapGridCenterY = gridMarginY + mapGridHeight / 2;

        mapSprite.x = mapGridCenterX;
        mapSprite.y = mapGridCenterY;
        mapSprite.scale.set(0.75);
        mapSprite.anchor.set(0.5, 0.5);
        mapSprite.zIndex = -1000;
        world.addChild(mapSprite);

        // Map overlay
        const overlayPath = `/assets/maps/overlay/default.png`;
        const mapOverlay = await loadAssetIfNeeded(overlayPath);
        const mapOverlaySprite = new Sprite(mapOverlay);
        mapOverlaySprite.x = mapGridCenterX;
        mapOverlaySprite.y = mapGridCenterY;
        mapOverlaySprite.scale.set(0.5);
        mapOverlaySprite.anchor.set(0.5, 0.5);
        mapOverlaySprite.zIndex = 9999;

        if (overlayWorld) {
          overlayWorld.addChild(mapOverlaySprite);
        } else {
          world.addChild(mapOverlaySprite);
        }

        // Draw grid
        const grid = new Graphics();
        grid.alpha = 0.005;

        for (let y = 0; y < GRID_HEIGHT; y++) {
          for (let x = 0; x < GRID_WIDTH; x++) {
            const isEven = (x + y) % 2 === 0;
            const color = isEven ? 0x444444 : 0x666666;
            grid.rect(
              gridMarginX + x * gridTileSize,
              gridMarginY + y * gridTileSize,
              gridTileSize,
              gridTileSize,
            );
            grid.fill(color);
          }
        }
        grid.zIndex = -500;
        world.addChild(grid);

        // Recalculate camera
        const mapWidth = 480;
        const mapHeight = 960;
        const scaleX = gameContainer.clientWidth / mapWidth;
        const scaleY = gameContainer.clientHeight / mapHeight;
        let scale = Math.min(scaleX, scaleY, 1);
        if (isXsScreen()) {
          scale = scale * 0.75;
        }

        world.scale.set(scale);
        world.position.set(
          (gameContainer.clientWidth - mapWidth * scale) / 2,
          (gameContainer.clientHeight - mapHeight * scale) / 2,
        );

        if (overlayWorld) {
          overlayWorld.scale.set(scale);
          overlayWorld.position.set(
            (gameContainer.clientWidth - mapWidth * scale) / 2,
            (gameContainer.clientHeight - mapHeight * scale) / 2,
          );
        }

        // Create Pokemon instances
        const newPokemons: Record<string, ClientPokemon> = {};
        const initPromises: Promise<void>[] = [];

        // Team A
        for (const member of log.teamA.members) {
          const pokemon = new ClientPokemon(
            member.battleData,
            member.initialPosition,
            "A",
            app,
            world,
          );
          newPokemons[member.pokemon.id] = pokemon;
          initPromises.push(pokemon.load());
        }

        // Team B
        for (const member of log.teamB.members) {
          const pokemon = new ClientPokemon(
            member.battleData,
            member.initialPosition,
            "B",
            app,
            world,
          );
          newPokemons[member.pokemon.id] = pokemon;
          initPromises.push(pokemon.load());
        }

        await Promise.all(initPromises);

        // Setup interaction for existing pokemons
        Object.values(newPokemons).forEach((p) => {
          p.setOnClick((clickedPokemon) => {
            console.log("Clicked pokemon:", clickedPokemon.id);
            setSelectedPokemonId(clickedPokemon.id.toString());
          });
        });

        setPokemons(newPokemons);
        flushSync(() => {
          setBattlePhase("ready");
        });
      } catch (error) {
        console.error("[Raid] Battle setup failed:", error);
      }
    },
    [app, world, overlayWorld],
  );

  // Trigger battle setup when log is available
  useEffect(() => {
    if (
      battleLog &&
      app &&
      world &&
      overlayWorld &&
      !hasStartedBattle.current
    ) {
      hasStartedBattle.current = true;
      setupBattle(battleLog);
    }
  }, [battleLog, app, world, overlayWorld, setupBattle]);

  // ============================================================================
  // Battle Playback
  // ============================================================================

  useEffect(() => {
    // Wait for battle transition to complete before starting replay
    if (
      battlePhase !== "ready" ||
      visualPhase !== "idle" ||
      !battleLog ||
      Object.keys(pokemons).length === 0
    ) {
      return;
    }

    const playBattle = async () => {
      const routines: Record<string, Battle_Action[]> = {};
      for (const member of battleLog.teamA.members) {
        routines[member.pokemon.id] = member.actions;
      }
      for (const member of battleLog.teamB.members) {
        routines[member.pokemon.id] = member.actions;
      }

      // Add a small delay before starting replay for better UX
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setBattlePhase("playing");

      let lastTickUpdate = 0;
      const replay = new BattleReplay(pokemons, (tick) => {
        // Throttle tick updates to React state (e.g. every 100ms)
        const now = Date.now();
        if (now - lastTickUpdate > 100) {
          setCurrentTick(tick);
          lastTickUpdate = now;
        }
      });

      await replay.play(
        routines,
        battleLog.statsPerTick,
        battleLog.events,
        battleLog.mana,
      );

      // Wait before showing completion
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setBattlePhase("completed");

      // Auto-acknowledge after delay (transition overlay is cosmetic only)
      await new Promise((resolve) => setTimeout(resolve, 5000));
      acknowledgeBattleEnd();
    };

    playBattle();
  }, [battlePhase, visualPhase, battleLog, pokemons, acknowledgeBattleEnd]);

  // ============================================================================
  // Navigation Guard (exit raid when finished or error)
  // ============================================================================

  useEffect(() => {
    // If we have a result, don't redirect (allow user to see defeat/victory screen)
    if (result) return;

    if (status === "idle" || status === "error") {
      console.warn(
        "[Raid] Prevention of redirect. Status:",
        status,
        "Error:",
        raidState.error,
      );
      // setCurrentRoute(undefined);
    }
  }, [status, setCurrentRoute, result, raidState.error]);

  // ============================================================================
  // Render
  // ============================================================================

  // Determine if we should show the battle canvas prominently
  const isBattleActive =
    status === "battle_starting" ||
    status === "in_battle" ||
    status === "battle_result";

  // Show canvas at full opacity during room selection (PIXI scene) or battle
  const isCanvasActive =
    isBattleActive ||
    (status === "room_selection" && isRoomSelectionSceneReady) ||
    (status === "draft" && roomSelectionSceneRef.current);

  return (
    <div
      className="flex flex-col h-full w-full bg-[#161e29] overflow-hidden relative"
      style={{
        maxWidth: "480px",
        height: "min(100svh, calc(480px * 18 / 9))",
      }}
    >
      {/* Persistent PixiJS Canvas Container */}
      <div
        ref={containerRef}
        id="raid-game-container"
        className={classNames(
          "absolute inset-0 transition-opacity duration-300 ",
          {
            "opacity-100": isCanvasActive,
            "opacity-30": !isCanvasActive,
          },
        )}
      />

      {/* Phase-specific UI Overlays */}
      <div className="relative z-10 flex flex-col h-full w-full">
        {/* Visual Overlays (Cinematics) */}
        {visualPhase === "intro" && (
          <div className="absolute inset-0 z-[100]">
            <RaidIntroView
              raidName={raidState.raid_name || "Raid"}
              onComplete={() => {
                console.log(
                  "[Raid] Intro complete, setting visual phase to idle",
                );
                // Mark that intro has completed - room selection scene can now initialize
                hasCompletedIntroRef.current = true;
                setVisualPhase("idle");
              }}
            />
          </div>
        )}

        {/* Standard UI States */}
        {(status === "starting" || status === "connecting") && (
          <RaidLoadingOverlay />
        )}

        {status === "lobby" && <RaidLobbyView />}

        {/* Room Selection V2 - Immersive Crossroads with HTML Overlay */}
        {status === "room_selection" && isRoomSelectionSceneReady && (
          <div className="absolute inset-0 z-20">
            <RaidRoomSelectionOverlayV2 isVisible={showRoomSelectionOverlay} />
          </div>
        )}

        {/* Room Selection Loading State (while PIXI scene initializes) */}
        {status === "room_selection" && !isRoomSelectionSceneReady && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-pk-gray-1/80">
            <div className="text-pk-gray-4 text-lg animate-pulse">
              Loading rooms...
            </div>
          </div>
        )}

        {/* Draft View (Hide if revealing room) - PIXI overlay with compact roster */}
        {status === "draft" && visualPhase !== "room_reveal" && (
          <RaidTeamBuildPixiOverlay
            roomSelectionScene={roomSelectionSceneRef.current}
            isVisible={true}
          />
        )}

        {status === "battle_starting" && (
          <RaidBattleHeader
            currentRound={current_round}
            isBossRound={is_boss_round}
            phase="loading"
          />
        )}

        {status === "in_battle" && (
          <>
            <RaidBattleHeader
              currentRound={current_round}
              isBossRound={is_boss_round}
              phase={battlePhase}
            />
            {battlePhase === "ready" || battlePhase === "playing" ? (
              <>
                {!selectedPokemonId && (
                  <BattleStatsHUD
                    battleState={pokemons}
                    battleStats={battleLog?.statsPerTick || {}}
                    currentTick={currentTick}
                  />
                )}

                {selectedPokemonId &&
                  pokemons[selectedPokemonId] &&
                  battleLog && (
                    <PokemonBattleHUD
                      pokemon={pokemons[selectedPokemonId]}
                      battleLog={battleLog}
                      currentTick={currentTick}
                      onClose={() => setSelectedPokemonId(null)}
                      onNext={handleNext}
                      onPrevious={handlePrevious}
                    />
                  )}
              </>
            ) : null}

            {/* Room Clear Transition Overlay (only on victory) */}
            {battlePhase === "completed" && battleLog?.winner === "teamA" && (
              <RaidRoomClearTransition
                myPokemon={Object.values(pokemons).filter(
                  (p) => p.Team === "A",
                )}
              />
            )}
          </>
        )}

        {status === "battle_result" && (
          <RaidBattleHeader
            currentRound={current_round}
            isBossRound={is_boss_round}
            phase="completed"
          />
        )}

        {(status === "finished" || !!result) && <RaidRewardsOverlay />}

        {status === "error" && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 text-white">
            <div className="bg-pk-red-5/90 border-2 border-pk-red-4 p-8 rounded-lg max-w-md text-center">
              <h2 className="text-2xl font-bold mb-4 font-vck text-pk-red-1">
                Raid Error
              </h2>
              <p className="mb-4">
                {raidState.error?.message || "Unknown error"}
              </p>
              <p className="text-sm opacity-75 font-mono">
                Code: {raidState.error?.code}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Preload Progress Indicator (subtle, bottom corner) */}
      {preloadState.isPreloading && (
        <div className="absolute bottom-4 right-4 z-50 bg-black/70 px-3 py-2 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-pk-blue-4 border-t-transparent rounded-full animate-spin" />
            <span className="text-pk-gray-4 text-xs">
              Preloading... {preloadState.progress.toFixed(0)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Battle Header Component
// ============================================================================

interface RaidBattleHeaderProps {
  currentRound: number;
  isBossRound: boolean;
  phase: BattlePhase;
}

const RaidBattleHeader: React.FC<RaidBattleHeaderProps> = ({
  currentRound,
  isBossRound,
  phase,
}) => {
  return (
    <div className="bg-pk-gray-2 border-b-4 border-pk-gray-1 px-4 py-3 flex items-center justify-between">
      {/* Round Indicator */}
      <div className="flex items-center gap-3">
        <div
          className={classNames(
            "px-3 py-1 rounded-md border",
            isBossRound
              ? "bg-pk-red-3/20 border-pk-red-4/40"
              : "bg-pk-fighting/20 border-pk-fighting/40",
          )}
        >
          <span
            className={classNames(
              "text-xs font-bold uppercase tracking-wider",
              isBossRound ? "text-pk-red-4" : "text-pk-fighting",
            )}
          >
            {isBossRound ? "BOSS" : "BATTLE"}
          </span>
        </div>
        <StrokedText
          fontSize={22}
          strokeWidth={4}
          strokeColor="#232020"
          fillColor="#f4eae7"
        >
          {isBossRound ? "BOSS ROOM" : `Room ${currentRound + 1}/10`}
        </StrokedText>
      </div>

      {/* Phase Indicator */}
      <div
        className={classNames(
          "flex items-center gap-2 px-3 py-1 rounded-full border",
          {
            "bg-pk-blue-3/30 border-pk-blue-4/50":
              phase === "idle" || phase === "loading",
            "bg-pk-yellow-1/30 border-pk-yellow-3/50": phase === "ready",
            "bg-pk-green-2/30 border-pk-green-4/50": phase === "playing",
            "bg-pk-purple-3/30 border-pk-purple-4/50": phase === "completed",
          },
        )}
      >
        {phase === "playing" && (
          <span className="w-2 h-2 bg-pk-green-4 rounded-full animate-pulse" />
        )}
        <span
          className={classNames("text-xs font-bold uppercase", {
            "text-pk-blue-4": phase === "idle" || phase === "loading",
            "text-pk-yellow-3": phase === "ready",
            "text-pk-green-4": phase === "playing",
            "text-pk-purple-4": phase === "completed",
          })}
        >
          {(phase === "idle" || phase === "loading") && "Loading"}
          {phase === "ready" && "Ready"}
          {phase === "playing" && "Live"}
          {phase === "completed" && "Complete"}
        </span>
      </div>
    </div>
  );
};

export default Raid;
